<template>
  <!-- Dismissible promo ribbon at the very top of the page, pointing at the API.
       After Supabase's event banner: one line of copy between two animated
       bracket fields that fade out toward it — openers on the left, closers
       on the right (atoms/BracketField.vue).

       Fixed over the generator, whose layout already leaves room for it. With
       `sticky` it takes its own height at the top of the page instead and
       stays pinned while the rest scrolls, so the content pages don't need
       to reserve space for it — or give it back once it's dismissed. -->
  <div
    v-if="!dismissed"
    class="site-ribbon top-0 z-50 flex h-12 items-center justify-center overflow-hidden border-b border-white/10 bg-gray-950 px-11"
    :class="sticky ? 'sticky' : 'fixed inset-x-0'"
  >
    <BracketField class="site-ribbon__field left-0" :rows="3" :font-size="12" fade="right" interactive />
    <BracketField class="site-ribbon__field right-0" :rows="3" :font-size="12" anchor="right" fade="left" invert interactive />

    <p class="site-ribbon__copy relative flex min-w-0 items-center gap-x-2.5 whitespace-nowrap text-sm text-gray-300">
      <span class="font-semibold text-white sm:hidden">API for any app</span>
      <span class="hidden sm:inline">
        <span class="font-semibold text-white">API</span><span class="hidden md:inline">: integrate color, brand &amp; screenshot extraction into your app</span>
      </span>
      <span class="text-gray-600" aria-hidden="true">•</span>
      <TextLink :href="apiUrl('top_ribbon', route.path)" tone="accent" @click="apiRibbonClicked">
        Get API key
        <template #iconAfter>
          <ArrowUpRightIcon class="h-3.5 w-3.5" aria-hidden="true" />
        </template>
      </TextLink>
    </p>

    <CloseButton
      label="Dismiss"
      size="sm"
      class="site-ribbon__close absolute right-2 top-1/2 -translate-y-1/2"
      @click.prevent="dismissed = true"
    />
  </div>
</template>

<script setup>
import { ArrowUpRightIcon } from '@heroicons/vue/16/solid'
import { trackEvent } from '~/composables/analytics'
import BracketField from '~/components/atoms/BracketField.vue'
import CloseButton from '~/components/atoms/CloseButton.vue'
import TextLink from '~/components/atoms/TextLink.vue'
import { apiUrl } from '~/utils/site'

defineProps({
  sticky: { type: Boolean, default: false },
})

// Shared across pages, so dismissing it once keeps it gone for the rest of
// the visit rather than until the next brand page.
const dismissed = useState('site-ribbon-dismissed', () => false)

// Stamps the outbound link with the page the ribbon was showing on.
const route = useRoute()

const apiRibbonClicked = () => {
  try {
    trackEvent('Signup API Ribbon Clicked')
  } catch {}
}
</script>

<style scoped>
/* Each field keeps to its own edge and is gone well before the middle; on a
   narrow screen they reach the copy, which clears a patch of the ribbon's own
   background around itself so the text never sits on glyphs. */
.site-ribbon__field {
  position: absolute;
  top: 0;
  bottom: 0;
  width: min(28%, 22rem);
}

.site-ribbon__copy,
.site-ribbon__close {
  /* The ribbon's own background, feathered out around the element. */
  background-color: theme('colors.gray.950');
  box-shadow: 0 0 20px 16px theme('colors.gray.950');
}
.site-ribbon__copy {
  border-radius: 9999px;
}
</style>
